// Tooltip content (lib/tip.js) for a unit and for a team, shared by the bracket, the spotlight and the team panels.
// With game data (`gd`) a unit's tip is the old bracket hover's whole story: stats, evasion, each weapon's and ability's
// damage (lib/unitOutput.js), elements, gear statuses, and zodiac against the foes and its own team (non-neutral only).
// Callers pass the grades they already cached (unitGrade / teamGrade), so hovering costs nothing new.
import { topParts } from './unitGrade.js';
import { unitOutput } from './unitOutput.js';
import { zodiacCompat } from './zodiac.js';
import { fmtStat } from './statDisplay.js';
import { wrSegment } from './wrPref.js';
import { unitInfo, gearStatusLine, gearElementLine } from '../components/spotlight/unitInfo.js';

const ZODIAC_COLOR = { Best: '#8fe0a0', Good: '#8fe0a0', Bad: '#f08a7a', Worst: '#f08a7a' };
const sign = (n) => (n > 0 ? '+' : '') + n;

export function unitTip(u, { team = '', color = null, grade = null, foot = null, gd = null, foes = null, foeLabel = '', mates = null } = {}) {
  const monster = u.Gender === 'Monster';
  const out = gd ? unitOutput(u, gd) : null;
  const dmg = (w) => w.name + (w.out ? ' (' + w.out + ')' : '');
  // each piece with ▲/▼ and its gap to the average when win-rate colors are on (lib/wrPref.js wrSegment)
  const seg = (dim, v, text) => (v ? [wrSegment(u, dim, v, text)] : null);
  const gear = monster ? null : out ? out.weapons.map((w) => wrSegment(u, 'Weapon', w.name, dmg(w))) : ['Mainhand', 'Offhand'].filter((k) => u[k]).map((k) => wrSegment(u, 'Weapon', u[k]));
  const armor = monster ? null : [['Head', u.Head], ['Armor', u.Armor]].filter(([, v]) => v).map(([d, v]) => wrSegment(u, d, v));
  const chips = [];
  if (grade && grade.parts) {
    const { up, down } = topParts(grade.parts, 2);
    for (const p of up) chips.push({ text: p.label + ' ' + sign(p.contribution), color: '#8fe0a0' });
    for (const p of down) chips.push({ text: p.label + ' ' + sign(p.contribution), color: '#f08a7a' });
  }
  let statLine = null, evadeLine = null, elementLine = null, statusLine = null;
  if (gd) {
    const info = unitInfo(u, gd), st = info.stats;
    statLine = `HP ${st.hp} · MP ${st.mp} · PA ${st.pa} · MA ${st.ma} · Sp ${st.speed} · Mv ${fmtStat('move', st.move)} · Jp ${fmtStat('jump', st.jump)}`;
    evadeLine = `F ${info.evade.front}% · S ${info.evade.side}% · B ${info.evade.back}% · Magic ${info.evade.magic}%`;
    elementLine = gearElementLine(u, info.equipped);
    statusLine = gearStatusLine(u, info.equipped);
  }
  const zodiac = (list, skip) => (list || []).filter((t) => !(skip && t === u)).map((t) => {
    const c = zodiacCompat(u.Gender, u.Sign, t.Gender, t.Sign);
    return { text: t.Name + ' ' + c, color: ZODIAC_COLOR[c] || null };
  }).filter((c) => !/ Neutral$/.test(c.text));
  return {
    kicker: [team, u.RaidBoss ? '★ Raid boss' : ''].filter(Boolean).join(' · '),
    title: u.Name,
    color,
    sub: (monster ? 'Monster · ' + u.Class : (u.Gender || '') + ' ' + u.Class) + (u.Sign ? ' · ' + u.Sign : ''),
    rows: [
      ['Grade', grade ? '~' + grade.score + '% expected' : null, null, grade && { text: grade.grade, color: grade.color }],
      ['Brave / Faith', u.Brave != null ? u.Brave + ' / ' + u.Faith : null],
      ['Stats', statLine],
      ['Evade', evadeLine],
      ['Weapon', gear],
      ['Secondary', seg('ActionSkill', u.ActionSkill)],
      ['Reaction', seg('ReactionSkill', u.ReactionSkill)],
      ['Support', seg('SupportSkill', u.SupportSkill)],
      ['Move', seg('MoveSkill', u.MoveSkill)],
      ['Armor', armor],
      ['Accessory', monster ? null : seg('Accessory', u.Accessory)],
      ['Elements', elementLine, '#f3d68e'],
      ['Gear status', statusLine, '#9fc4e2'],
    ],
    pairs: out ? out.abilities.map((a) => [a.name, a.out]) : null,
    groups: [
      { label: 'Zodiac vs ' + (foeLabel || 'foes'), chips: zodiac(foes, false) },
      { label: 'With team', chips: zodiac(mates, true) },
    ],
    chips,
    lines: out && out.abilities.some((a) => a.out) ? ['Damage vs a 60-Faith target, before evasion, elements and zodiac.'] : [],
    foot,
  };
}

// A team: its grade, and each unit with its own grade.
export function teamTip(label, units, { color = null, tg = null, kicker = 'Team', lines = [], foot = null } = {}) {
  return {
    kicker,
    title: label,
    color,
    rows: [['Team grade', tg ? '~' + tg.score + '% expected' : null, null, tg && { text: tg.grade, color: tg.color }]],
    chips: (units || []).map((u, i) => {
      const g = tg && tg.units && tg.units[i];
      return { text: u.Name + ' · ' + u.Class, grade: g ? { text: g.grade, color: g.color } : null };
    }),
    lines,
    foot,
  };
}
